<template>
  <div>
    <!-- In the conversation layout the brand row carries the logo and the back link instead. -->
    <PageHeader v-if="!showChat" />
    <div v-if="loading">
      <Spinner>{{ t('loading') }}...</Spinner>
    </div>
    <div v-else>
      <h2 v-if="!showChat" class="mb-0">
        <router-link :to="backToSearch" class="text-body back-link">
          <b-icon icon="chevron-left" />
          <span class="back-link__label">{{ t('searchAnotherShipment') }}</span>
        </router-link>
      </h2>
      <!-- With the assistant, the tracking details are the first message of the conversation. -->
      <component
        :is="chatEnabled ? 'AssistantChat' : 'div'"
        v-if="itemData"
        class="parcel-info-block"
        v-bind="chatEnabled ? { trackingId: id, brand: brandName, baseUrl: assistantUrl, demoPostalCode } : {}"
        @verified="unlocked = true"
      >
        <template v-if="chatEnabled" #action>
          <router-link :to="backToSearch" class="back-link back-link--row" :aria-label="t('searchAnotherShipment')">
            <b-icon icon="chevron-left" />
            <span class="back-link__label">{{ t('assistant.newSearch') }}</span>
          </router-link>
        </template>
        <ShipmentTitle
          v-if="primaryShipment"
          :shipment="primaryShipment"
          :original-shipment="secondaryShipment"
          :item-data="itemData"
        />

        <TrackingStepper
          v-if="primaryShipment?.lastTrackingEventStatus || (isCharma && primaryShipment)"
          :status="primaryShipment?.lastTrackingEventStatus || INFO_RECEIVED"
        />

        <ExternalPartner v-if="itemData" :item-data="itemData" :events="trackingEvents" />

        <TrackingStatus v-if="lastEvent || (isCharma && primaryShipment)" :event="lastEvent" />

        <DeliveryInformation v-if="itemData" :item-data="itemData" :unlocked="unlocked" />

        <ProofOfDelivery v-if="proofOfDelivery" :image="proofOfDelivery" />

        <DimensionsInfo v-if="itemData" :item-data="itemData" />

        <ShipmentInfo
          v-if="primaryShipment"
          :shipment="primaryShipment"
          :original-shipment="secondaryShipment"
          :item-data="itemData"
        />

        <AddressMap v-if="point" :point="point" :item-data="itemData" />

        <TrackingEvents v-if="trackingEvents.length" :events="trackingEvents" />
      </component>
      <div v-else>
        <h2 class="mt-4 mb-0 text-black-50 parcel-direction">
          {{ t('cannotFindShipment') }}
        </h2>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PickupPoint, Shipment, TrackingData, TrackingEvent } from '@/types/Models/Shipment'
import { ref, defineComponent, computed, type ComputedRef, type Ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { SEARCH_PAGE } from '@/consts/Router'
import { INFO_RECEIVED } from '@/consts/TrackingEventStatuses'
import { getAppBrand, getBrandQuery } from '@/helpers/Branding'
import { mapCountryToLanguage } from '@/helpers/Utils'
import { browserLocale } from '@/plugins/i18n'
import { type TrackingStore, useTrackingStore } from '@/store/modules/tracking'
import moment from 'moment'
import BIcon from '@/views/Elements/Icons/BIcon.vue'
import Spinner from '@/views/Elements/Spinner.vue'
import AddressMap from '@/views/Components/AddressMap.vue'
import PageHeader from '@/views/Layouts/Parts/PageHeader.vue'
import ShipmentInfo from '@/views/Components/ShipmentInfo.vue'
import AssistantChat from '@/views/Components/Assistant/AssistantChat.vue'
import ShipmentTitle from '@/views/Components/ShipmentTitle.vue'
import DimensionsInfo from '@/views/Components/DimensionsInfo.vue'
import TrackingEvents from '@/views/Components/TrackingEvents.vue'
import TrackingStatus from '@/views/Components/TrackingStatus.vue'
import ExternalPartner from '@/views/Components/ExternalPartner.vue'
import ProofOfDelivery from '@/views/Components/ProofOfDelivery.vue'
import TrackingStepper from '@/views/Components/TrackingStepper.vue'
import DeliveryInformation from '@/views/Components/DeliveryInformation.vue'

export default defineComponent({
  name: 'TrackingNumberInfo',
  components: {
    BIcon,
    Spinner,
    PageHeader,
    AddressMap,
    ShipmentInfo,
    ShipmentTitle,
    DimensionsInfo,
    TrackingEvents,
    TrackingStatus,
    TrackingStepper,
    ExternalPartner,
    ProofOfDelivery,
    DeliveryInformation,
    AssistantChat,
  },
  setup() {
    const { t, locale } = useI18n()
    const route = useRoute()
    const trackingStore: TrackingStore = useTrackingStore()
    const loading: Ref<boolean> = ref(true)
    const isFetching: Ref<boolean> = ref(false)

    const itemData: ComputedRef<TrackingData | null> = computed(() => trackingStore.trackingInfo)
    const proofOfDelivery: ComputedRef<string | null> = computed(() => trackingStore.proofOfDelivery)
    const point: ComputedRef<PickupPoint | null> = computed(() => itemData.value?.pickupPoint || null)
    const id: ComputedRef<string | undefined> = computed(() => route.params?.id as string | undefined)

    const trackingEvents: ComputedRef<TrackingEvent[]> = computed(() =>
      !itemData.value?.shipments
        ? []
        : [
            ...(itemData.value?.shipments[0]?.trackingEvents || []),
            ...(itemData.value?.shipments[1]?.trackingEvents || []),
          ].sort((a: TrackingEvent, b: TrackingEvent) => moment(b.eventTime).diff(moment(a.eventTime))),
    )

    const lastEvent: ComputedRef<TrackingEvent | null> = computed(() =>
      trackingEvents.value.length ? trackingEvents.value[0] || null : null,
    )

    const primaryShipment: ComputedRef<Shipment | null> = computed(() =>
      itemData.value?.shipments
        ? [...(itemData.value?.shipments || [])].sort((a, b) => b.runningNumber - a.runningNumber)[0] || null
        : null,
    )

    const secondaryShipment: ComputedRef<Shipment | null> = computed(() =>
      itemData.value?.shipments.length
        ? itemData.value?.shipments.find((s: Shipment) => s !== primaryShipment.value) || null
        : null,
    )

    const loadData = () => {
      if (isFetching.value) {
        return
      }
      const identifier = id.value
      if (!identifier) {
        return
      }
      isFetching.value = true
      loading.value = true

      trackingStore.fetch(identifier)

      // when trackingInfo updates, compute trackingNumber and fetch pod
      const stop = watch(
        () => trackingStore.trackingInfo,
        (res) => {
          if (!res) return
          // Recipients in Sweden and Finland get Swedish and Finnish; everyone else gets their browser language
          const countryLocale = mapCountryToLanguage(res.recipient.countryCode)
          locale.value = countryLocale !== 'en' ? countryLocale : browserLocale() || 'en'
          trackingStore.translateEventTexts(locale.value)
          const original = res.shipments?.find((s: Shipment) => s.type === 'original')?.shipmentNumber || identifier
          trackingStore.fetchProofOfDelivery(original)

          // mark sms as read
          if (route.query && route.query.r) {
            trackingStore.markAsRead(res.id)
          }

          if (loading.value) {
            loading.value = false
          }
          isFetching.value = false
          stop()
        },
        { immediate: false },
      )
    }

    watch(id, loadData, { immediate: true })

    const brandNames = { default: '21grams', charma: 'Charma', virea: 'Virea' } as const
    const assistantUrl: string = import.meta.env.VITE_ASSISTANT_URL || ''
    const chatEnabled = computed(() => !!assistantUrl && !!id.value)
    const showChat = computed(() => chatEnabled.value && !loading.value && !!itemData.value)
    // Set when the recipient's postal code is verified in the assistant; the full address is shown then.
    const unlocked = ref(false)
    watch(id, () => (unlocked.value = false))
    // Dev demo only (VITE_ASSISTANT_DEMO=1 in the dev deploy): show testers the recipient's postal code.
    const demoPostalCode = computed(() =>
      import.meta.env.VITE_ASSISTANT_DEMO === '1' ? itemData.value?.recipient?.postalCode || '' : '',
    )
    return {
      assistantUrl,
      chatEnabled,
      showChat,
      demoPostalCode,
      unlocked,
      brandName: brandNames[getAppBrand()],
      id,
      isCharma: getAppBrand() === 'charma',
      INFO_RECEIVED,
      t,
      point,
      loading,
      itemData,
      lastEvent,
      trackingEvents,
      proofOfDelivery,
      primaryShipment,
      secondaryShipment,
      backToSearch: { name: SEARCH_PAGE, query: getBrandQuery() },
    }
  },
})
</script>
